/* ============================================================
   ILS — Weight formatter & "weight-option name" dictionary
   (single source of truth shared by Liquid + JS).

   - window.ILS.formatWeight(rawValue, langIso)
       mirror of snippets/ils-weight.liquid (gram parsing → g/kg,
       comma decimal, cyrillic units for ru).
   - window.ILS.localizeOptionName(name, langIso)
       mirror of snippets/ils-option-name.liquid (returns localized
       label for the "weight/volume" option, pass-through otherwise).
   - On DOM ready: walks <variant-selects> on the product page and
     rewrites option legends/labels + visible option values + the
     selected-value display. Pure DOM rewrite — does not interfere
     with Dawn's <variant-selects> add-to-cart wiring (the radio
     `value` / <option> `value` attributes are kept verbatim).

   Loaded via include hook in theme.liquid; see _claude/PATCHES.md.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Language ---------- */
  function currentLangIso() {
    try {
      if (window.Shopify && Shopify.locale) return String(Shopify.locale);
    } catch (e) {}
    const docLang = (document.documentElement && document.documentElement.lang) || '';
    return String(docLang || '');
  }

  /* ---------- Weight formatter ----------
     Mirror of snippets/ils-weight.liquid — KEEP IN SYNC.

     TASK-1990: variant titles like "4 kg" / "3 kg" use a "kg" unit suffix
     instead of the standard "Ng" grams format.
     TASK-2632: on Russian (and some EU) markets the value arrives TRANSLATED,
     e.g. "4 кг" (cyrillic unit) and "1.000 г 😍" / "1 000 гр" (thousands
     separator). Detect the cyrillic "кг" suffix AND fold thousands separators
     (space / '.' / ',' + 3-digit group) so "1.000 г" reads as 1000 g → "1 кг"
     instead of stopping at the separator and reading "1" → "1 г". A short
     (1-2 digit) '.'/',' group stays a real decimal (archived "2.25 kg"). */
  function parseGrams(src, isKg) {
    var m = String(src).match(/\d[\d.,\s]*/); /* \s covers nbsp/narrow-nbsp in JS */
    if (!m) return null;
    var tok = m[0], intPart = '', fracPart = '', haveDot = false;
    for (var i = 0; i < tok.length; i++) {
      var ch = tok[i];
      if (ch >= '0' && ch <= '9') {
        if (haveDot) fracPart += ch; else intPart += ch;
      } else if (ch === '.' || ch === ',') {
        if (haveDot) { intPart += fracPart; fracPart = ''; } /* fold earlier thousands group */
        haveDot = true;
      } else { /* space / nbsp = thousands delimiter — skip; digits after it
                  continue the integer, a space before the unit is ignored (so
                  "2.25 kg" keeps its decimal instead of folding to "225"). */ }
    }
    if (intPart === '') return null;
    var intVal = parseInt(intPart, 10) || 0;
    if (fracPart === '') return isKg ? intVal * 1000 : intVal;
    if (isKg) return intVal * 1000 + parseInt((fracPart + '000').slice(0, 3), 10);
    if (fracPart.length === 3) return intVal * 1000 + parseInt(fracPart, 10);
    return intVal * 1000 + parseInt((fracPart + '000').slice(0, 3), 10);
  }
  function formatWeight(rawValue, langIso) {
    const src = (rawValue == null ? '' : String(rawValue));
    const isKg = /kg|кг/i.test(src);
    const grams = parseGrams(src, isKg);
    if (grams == null) return src;
    const lang = String(langIso || currentLangIso() || '').toLowerCase();
    const isRu = lang.indexOf('ru') === 0;
    const uG = isRu ? 'г' : 'g';
    const uKg = isRu ? 'кг' : 'kg';
    if (grams < 1000) return grams + ' ' + uG;
    const kgInt = Math.floor(grams / 1000);
    const rem = grams % 1000;
    if (rem === 0) return kgInt + ' ' + uKg;
    const frac = String(rem).padStart(3, '0').replace(/0+$/, '');
    return kgInt + ',' + frac + ' ' + uKg;
  }

  /* ---------- Option-name dictionary ----------
     Keep in sync with snippets/ils-weight.liquid +
     snippets/ils-option-name.liquid WEIGHT_ALIASES set. Includes
     Shopify Markets / Translate-&-Adapt variants (Volumen, Tūris,
     Maht) the merchant may have applied per-locale. */
  const WEIGHT_ALIASES = new Set([
    'apjoms', 'svars',
    'объём', 'объем', 'вес',
    'volume', 'weight',
    'menge', 'gewicht', 'volumen',
    'kiekis', 'svoris', 'tūris', 'turis',
    'kogus', 'kaal', 'maht'
  ]);
  const OPTION_NAME_BY_LANG = {
    ru: 'Объём',
    lv: 'Apjoms',
    en: 'Volume',
    de: 'Menge',
    lt: 'Kiekis',
    et: 'Kogus'
  };
  function isWeightOption(name) {
    return WEIGHT_ALIASES.has(String(name || '').trim().toLowerCase());
  }
  function localizeOptionName(name, langIso) {
    if (!isWeightOption(name)) return String(name == null ? '' : name);
    const lang = String(langIso || currentLangIso() || '').toLowerCase().split('-')[0];
    return OPTION_NAME_BY_LANG[lang] || String(name);
  }

  /* ---------- Weight-tile row layout (single source of truth) ----------
     Decides how N weight tiles (1..N) are split into rows for the unified
     layout used in three places (PDP inline tiles, catalog quick-add popup,
     cart-drawer weight popup). Returns an array of row sizes whose sum is N.

       1 → [1]   (rendered in a 2-col grid with the second cell empty →
                  pill visually occupies half the row; CSS handles that)
       2 → [2]   3 → [3]   4 → [2,2]
       5 → [3,2]    6 → [3,3]   7 → [4,3]   8 → [4,4]
       9 → [3,3,3]  10 → [4,3,3]
       N>10 → rows of max 4 with a leftover last row (defensive fallback)

     TASK-1988: 4 now splits into two rows of 2 (was one row of 4).
     The Liquid version of this split lives inline in
     sections/custom-main-product.liquid for the PDP tiles — KEEP IN SYNC. */
  function weightRows(n) {
    n = Math.max(0, parseInt(n, 10) || 0);
    if (n === 0) return [];
    switch (n) {
      case 1: return [1];
      case 2: return [2];
      case 3: return [3];
      case 4: return [2, 2];
      case 5: return [3, 2];
      case 6: return [3, 3];
      case 7: return [4, 3];
      case 8: return [4, 4];
      case 9: return [3, 3, 3];
      case 10: return [4, 3, 3];
      default: {
        var rows = [];
        var rem = n;
        while (rem > 4) { rows.push(4); rem -= 4; }
        if (rem > 0) rows.push(rem);
        return rows;
      }
    }
  }

  window.ILS = window.ILS || {};
  window.ILS.formatWeight = formatWeight;
  window.ILS.localizeOptionName = localizeOptionName;
  window.ILS.isWeightOption = isWeightOption;
  window.ILS.weightRows = weightRows;

  /* ============================================================
     Product page: rewrite the variant-picker UI in place.
     Targets Dawn's <variant-selects>:
       .product-form__input--dropdown  → <label> + <select><option>…
       .product-form__input--pill      → <legend> + <input radio>/<label>
       .product-form__input--swatch    → <legend>…<span data-selected-value>
     `value` attributes on inputs/options are kept verbatim so that
     Dawn's variant lookup keeps working.
     ============================================================ */
  function processOptionInput(input) {
    if (!input || input.dataset.ilsWeightDone === '1') return;

    /* The option name lives on Dawn-built <input>/<option> via data-option-name
       (see product-variant-options.liquid). For dropdowns we read from the
       <select>'s name attribute as fallback. */
    let optionName = '';
    const firstWithName = input.querySelector('[data-option-name]');
    if (firstWithName) optionName = firstWithName.getAttribute('data-option-name') || '';
    if (!optionName) {
      const sel = input.querySelector('select[name^="options["]');
      if (sel) {
        const m = sel.getAttribute('name').match(/options\[(.+)\]/);
        if (m) optionName = m[1];
      }
    }
    if (!isWeightOption(optionName)) return;

    /* Rewrite legend / label text.
       TASK-1886: skip our PDP custom legend (.ils-pp__opt-label). That
       legend is server-rendered from the theme locale key
       `products.product.choose_weight` ("Choose weight" / localized) and
       must NOT be replaced with the localized option name (which for
       en-eu resolves "Apjoms" → "Volume" via OPTION_NAME_BY_LANG below).
       Other Dawn pickers (legacy variant-selects on quick-view, etc.)
       still get the legacy rewrite for backwards compatibility. */
    const legend = input.querySelector('legend.form__label, label.form__label');
    if (legend && !legend.classList.contains('ils-pp__opt-label')) {
      /* Preserve trailing ":" if it was there + any nested data-selected-value span. */
      const selectedSpan = legend.querySelector('[data-selected-value]');
      const hadColon = /:\s*$/.test(legend.textContent || '') || !!selectedSpan;
      const localized = localizeOptionName(optionName);
      if (selectedSpan) {
        /* Wipe everything before the span, then prepend localized label. */
        while (legend.firstChild && legend.firstChild !== selectedSpan) {
          legend.removeChild(legend.firstChild);
        }
        legend.insertBefore(document.createTextNode(localized + ': '), selectedSpan);
        /* Format the selected value too. */
        selectedSpan.textContent = formatWeight(selectedSpan.textContent || '');
      } else {
        legend.textContent = hadColon ? (localized + ':') : localized;
      }
    }

    /* Format option text — read the AUTHORITATIVE raw value off the
       <option value="…"> / <input value="…"> attribute so the rewrite
       is idempotent (re-running yields the same result, never compounds
       on a value that the server already formatted via ils-weight). */
    const isSwatchContainer = input.classList.contains('product-form__input--swatch');
    input.querySelectorAll('select > option').forEach(function (opt) {
      const raw = opt.getAttribute('value') || '';
      const formatted = formatWeight(raw);
      if (formatted && opt.textContent !== formatted) opt.textContent = formatted;
    });
    if (!isSwatchContainer) {
      input.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        const lbl = input.querySelector('label[for="' + radio.id + '"]');
        if (!lbl) return;
        const raw = radio.getAttribute('value') || '';
        const formatted = formatWeight(raw);
        /* Only rewrite labels whose visible text comes from a leading
           text node (Dawn's default picker). Custom pickers that wrap
           the value in a <span> (e.g. .ils-pp__tile > .w-amt) are
           rendered correctly server-side by custom-main-product.liquid —
           do not touch them here. */
        const first = lbl.firstChild;
        if (first && first.nodeType === Node.TEXT_NODE && (first.textContent || '').trim()) {
          if (first.textContent.trim() !== formatted) first.textContent = formatted;
        }
      });
    }

    input.dataset.ilsWeightDone = '1';
  }

  function processAll(root) {
    const scope = root || document;
    scope.querySelectorAll('.product-form__input').forEach(processOptionInput);
  }

  /* Re-run on variant change — Dawn re-renders selected-value text. */
  function wireSelectedValueRefresh() {
    document.addEventListener('change', function (e) {
      const input = e.target && e.target.closest && e.target.closest('.product-form__input');
      if (!input) return;
      const span = input.querySelector('[data-selected-value]');
      if (!span) return;
      span.textContent = formatWeight(span.textContent || '');
    });
  }

  /* Observe DOM additions (e.g. quick-view, AJAX section reload). */
  function observe() {
    const mo = new MutationObserver(function (muts) {
      for (const mut of muts) {
        mut.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('.product-form__input')) processOptionInput(node);
          if (node.querySelectorAll) {
            node.querySelectorAll('.product-form__input').forEach(processOptionInput);
          }
        });
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    processAll(document);
    wireSelectedValueRefresh();
    observe();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
